import { useEffect, useState } from "react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { SlotGrid } from "@/components/booking/SlotGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALENDAR_CLASSES = {
  caption_label: "font-display text-base tracking-wide text-white",
  nav_button: "h-8 w-8 border border-white/15 bg-transparent text-slate-300 transition-colors hover:border-gold hover:text-gold inline-flex items-center justify-center",
  head_cell: "w-10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500",
  cell: "p-0.5 text-center",
  day: "h-10 w-10 font-sans text-sm text-slate-200 transition-colors hover:bg-white/5 hover:text-gold inline-flex items-center justify-center",
  day_selected: "!bg-gold !text-ink font-medium hover:!bg-gold",
  day_today: "border border-gold/40",
  day_disabled: "!text-slate-700 hover:!bg-transparent hover:!text-slate-700 cursor-not-allowed",
  day_outside: "!text-slate-700",
};

/** Calendar + slot grid. Calls onSelect(slot | null) whenever the chosen slot changes. */
export const SlotPicker = ({ date, onDateChange, slot, onSelect, excludeBookingId }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = new Date();

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    onSelect(null);
    setLoading(true);
    axios
      .get(`${API}/bookings/availability`, { params: { date: format(date, "yyyy-MM-dd"), exclude: excludeBookingId } })
      .then((r) => !cancelled && setSlots(r.data.slots))
      .catch(() => !cancelled && toast.error("Couldn't load availability — please try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, excludeBookingId]);

  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div data-testid="booking-calendar">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onDateChange}
          weekStartsOn={1}
          fromDate={today}
          toDate={addDays(today, 60)}
          disabled={[{ dayOfWeek: [0, 6] }, { before: today }]}
          classNames={CALENDAR_CLASSES}
          className="p-0"
        />
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
          Times shown in UK time (Europe/London)
        </p>
      </div>
      <SlotGrid date={date} slots={slots} loading={loading} selected={slot} onSelect={onSelect} />
    </div>
  );
};
